import React, { useState } from 'react';
import { OnboardingStatusResponse } from '../types';
import { parseDateOnly } from '../utils/fairStatus'; // Issue #1672

interface OnboardingFairReviewProps {
  token: string;
  onboardingData: OnboardingStatusResponse;
  onStepComplete: (step: string, data?: Record<string, unknown>) => Promise<void>;
  error: string | null;
}

/**
 * Fair review step for Fair Manager onboarding
 * Issue #1455 - Step 6 of 7: Review fair details and confirm
 */
const OnboardingFairReview: React.FC<OnboardingFairReviewProps> = ({
  onboardingData,
  onStepComplete,
}) => {
  const { fair } = onboardingData;
  const savedReview = onboardingData.onboarding.progress_data?.fair_review || {};

  const [fairDescription, setFairDescription] = useState(
    savedReview.fair_description || fair.description || ''
  );
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!confirmed) {
      setError('Please confirm that the fair information is correct.');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      await onStepComplete('fair_review', {
        fair_description: fairDescription.trim(),
      });

      // Move to completion step
      await onStepComplete('done');
    } catch (err) {
      setError('Failed to save fair review. Please try again.');
      console.error('Failed to save fair review:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    const date = parseDateOnly(dateString); // Issue #1672: handles YYYY-MM-DD as local time
    if (!date) return 'Not set';
    return date.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Review Your Fair</h2>
        <p className="text-gray-600">
          Review the details for <strong>{fair.name}</strong>. You'll be able to edit these details
          from your dashboard after onboarding.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        {/* Fair Details Summary */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Fair Details</h3>

          <dl className="space-y-4">
            <div>
              <dt className="text-sm font-medium text-gray-500">Fair Name</dt>
              <dd className="mt-1 text-base text-gray-900">{fair.name}</dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Location</dt>
              <dd className="mt-1 text-base text-gray-900">
                {fair.city && fair.state ? `${fair.city}, ${fair.state}` : 'Location not set'}
              </dd>
            </div>

            <div>
              <dt className="text-sm font-medium text-gray-500">Dates</dt>
              <dd className="mt-1 text-base text-gray-900">
                {fair.start_date || fair.end_date
                  ? `${formatDate(fair.start_date)} - ${formatDate(fair.end_date)}`
                  : 'Dates not set'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Fair Description */}
        <div className="mb-6">
          <label htmlFor="fairDescription" className="block text-sm font-medium text-gray-700 mb-2">
            Fair Description
          </label>
          <textarea
            id="fairDescription"
            value={fairDescription}
            onChange={(e) => {
              setFairDescription(e.target.value);
              setError(null);
            }}
            rows={6}
            placeholder="Describe your fair: What can vendors expect? What makes it special? Who attends? What products are featured?"
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            aria-describedby="fairDescription-description"
          />
          <p id="fairDescription-description" className="mt-1 text-sm text-gray-500">
            Help vendors understand what your fair is all about
          </p>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-md p-4 mb-6">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-blue-600 mr-2 flex-shrink-0 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clipRule="evenodd"
              />
            </svg>
            <div>
              <h4 className="text-sm font-semibold text-blue-900">Edit Anytime</h4>
              <p className="text-sm text-blue-800 mt-1">
                You'll be able to update all fair details, add booth configurations, set pricing,
                and manage vendor reservations from your Fair Manager Dashboard after completing
                onboarding.
              </p>
            </div>
          </div>
        </div>

        {/* Confirmation checkbox */}
        <div className="mb-6">
          <label className="flex items-start cursor-pointer">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(e) => {
                setConfirmed(e.target.checked);
                setError(null);
              }}
              className="mt-1 h-5 w-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
              aria-describedby="confirm-description"
            />
            <span className="ml-3 text-gray-700" id="confirm-description">
              I confirm that the fair information is correct and I'm ready to start managing this
              fair
            </span>
          </label>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4" role="alert">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className={`
              px-6 py-2 rounded-md font-medium transition-colors
              ${
                loading
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-teal-600 text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500'
              }
            `}
          >
            {loading ? 'Completing...' : 'Complete Onboarding'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OnboardingFairReview;
